/**
 * Star Force Enhancement System Configuration
 * MapleStory-style equipment enhancement with 15 stars, no destruction
 */

export const STAR_FORCE_CONFIG = {
  maxStars: 15,

  // Success rates for each star level (0→1, 1→2, etc.)
  successRates: [
    0.95, // 0→1
    0.90, // 1→2
    0.85, // 2→3
    0.80, // 3→4
    0.75, // 4→5
    0.70, // 5→6
    0.65, // 6→7
    0.60, // 7→8
    0.55, // 8→9
    0.50, // 9→10
    0.45, // 10→11
    0.40, // 11→12
    0.35, // 12→13
    0.30, // 13→14
    0.25, // 14→15
  ],

  // Base Star Essence costs for each enhancement level
  baseCosts: [
    5,    // 0→1
    8,    // 1→2
    12,   // 2→3
    18,   // 3→4
    25,   // 4→5
    35,   // 5→6
    50,   // 6→7
    70,   // 7→8
    100,  // 8→9
    140,  // 9→10
    200,  // 10→11
    280,  // 11→12
    400,  // 12→13
    550,  // 13→14
    750,  // 14→15
  ],

  // Cost multipliers by rarity
  rarityCostMultipliers: {
    common: 0.5,
    uncommon: 0.75,
    rare: 1.0,
    epic: 1.5,
    legendary: 2.0,
    mythic: 3.0,
  },

  // Failure behavior by star level
  // 0-5: Stay at same level
  // 6+: Drop 1 star (minimum 5)
  downgradeThreshold: 6,
  minimumStarOnDowngrade: 5,

  // Pity system
  pity: {
    bonusPerFailure: 0.10,  // +10% per consecutive failure
    maxBonus: 0.30,         // Max +30% bonus
  },

  // Safeguard mode (available 6-10 only)
  safeguard: {
    minStar: 6,
    maxStar: 10,
    costMultiplier: 2.0,  // 2x Star Essence cost
  },
};

// Stat bonuses per star by rarity tier
// Format: [ATK, DEF, HP] per star in each star range
export const STAR_FORCE_BONUSES = {
  common: {
    low: [1, 0.5, 5],      // Stars 1-5
    mid: [2, 1, 8],        // Stars 6-10
    high: [3, 1.5, 12],    // Stars 11-15
  },
  uncommon: {
    low: [2, 1, 8],
    mid: [3, 1.5, 12],
    high: [5, 2.5, 18],
  },
  rare: {
    low: [3, 1.5, 12],
    mid: [5, 2.5, 18],
    high: [8, 4, 25],
  },
  epic: {
    low: [5, 2.5, 18],
    mid: [8, 4, 25],
    high: [12, 6, 35],
  },
  legendary: {
    low: [8, 4, 25],
    mid: [12, 6, 35],
    high: [18, 9, 50],
  },
  mythic: {
    low: [12, 6, 35],
    mid: [18, 9, 50],
    high: [25, 12, 70],
  },
};

// Milestone bonuses at specific star levels
export const STAR_FORCE_MILESTONES = {
  5: {
    critChance: 0.02,  // +2% Crit Chance
  },
  10: {
    critDamage: 0.05,  // +5% Crit Damage
    allStats: 0.01,    // +1% All Stats (STR, DEX, INT, LUK)
  },
  15: {
    critChance: 0.05,  // +5% Crit Chance
    critDamage: 0.10,  // +10% Crit Damage
    allStats: 0.03,    // +3% All Stats
  },
};

// Star Essence drop rates by dungeon tier
export const STAR_ESSENCE_DROPS = {
  // Regular mobs
  mobs: {
    tier1: { min: 1, max: 2, chance: 0.30 },
    tier2: { min: 2, max: 4, chance: 0.40 },
    tier3: { min: 4, max: 6, chance: 0.50 },
    tier4: { min: 6, max: 10, chance: 0.60 },
    tier5: { min: 10, max: 15, chance: 0.70 },
  },
  // Boss mobs (guaranteed drops)
  bosses: {
    tier1: { min: 15, max: 25, chance: 1.0 },
    tier2: { min: 30, max: 50, chance: 1.0 },
    tier3: { min: 50, max: 80, chance: 1.0 },
    tier4: { min: 80, max: 120, chance: 1.0 },
    tier5: { min: 150, max: 250, chance: 1.0 },
  },
};

/**
 * Get the star tier for bonus calculation
 * @param {number} stars - Current star count
 * @returns {'low'|'mid'|'high'} Star tier
 */
export function getStarTier(stars) {
  if (stars <= 5) return 'low';
  if (stars <= 10) return 'mid';
  return 'high';
}

/**
 * Calculate the cost to enhance from current stars
 * @param {number} currentStars - Current star count (0-14)
 * @param {string} rarity - Item rarity
 * @param {boolean} safeguard - Whether safeguard is enabled
 * @returns {number} Star Essence cost
 */
export function getEnhancementCost(currentStars, rarity, safeguard = false) {
  if (currentStars >= STAR_FORCE_CONFIG.maxStars) return 0;

  const baseCost = STAR_FORCE_CONFIG.baseCosts[currentStars];
  const rarityMult = STAR_FORCE_CONFIG.rarityCostMultipliers[rarity] || 1.0;
  const safeguardMult = safeguard ? STAR_FORCE_CONFIG.safeguard.costMultiplier : 1.0;

  return Math.ceil(baseCost * rarityMult * safeguardMult);
}

/**
 * Get success rate for enhancement
 * @param {number} currentStars - Current star count
 * @param {number} consecutiveFailures - Number of consecutive failures
 * @returns {number} Success rate (0-1)
 */
export function getSuccessRate(currentStars, consecutiveFailures = 0) {
  if (currentStars >= STAR_FORCE_CONFIG.maxStars) return 0;

  const baseRate = STAR_FORCE_CONFIG.successRates[currentStars];
  const pityBonus = Math.min(
    consecutiveFailures * STAR_FORCE_CONFIG.pity.bonusPerFailure,
    STAR_FORCE_CONFIG.pity.maxBonus
  );

  return Math.min(1.0, baseRate + pityBonus);
}

/**
 * Check if safeguard is available for current star level
 * @param {number} currentStars - Current star count
 * @returns {boolean} Whether safeguard can be used
 */
export function canUseSafeguard(currentStars) {
  const { minStar, maxStar } = STAR_FORCE_CONFIG.safeguard;
  return currentStars >= minStar && currentStars <= maxStar;
}

/**
 * Calculate total stat bonuses for a given star count
 * @param {number} stars - Star count
 * @param {string} rarity - Item rarity
 * @returns {{attack: number, defense: number, health: number, critChance: number, critDamage: number, allStats: number}}
 */
export function calculateStarForceStats(stars, rarity) {
  const bonuses = STAR_FORCE_BONUSES[rarity] || STAR_FORCE_BONUSES.common;

  let attack = 0;
  let defense = 0;
  let health = 0;
  let critChance = 0;
  let critDamage = 0;
  let allStats = 0;

  // Calculate cumulative bonuses for each star
  for (let i = 1; i <= stars; i++) {
    const tier = getStarTier(i);
    const [atk, def, hp] = bonuses[tier];
    attack += atk;
    defense += def;
    health += hp;

    // Check for milestone bonuses
    const milestone = STAR_FORCE_MILESTONES[i];
    if (milestone) {
      if (milestone.critChance) critChance += milestone.critChance;
      if (milestone.critDamage) critDamage += milestone.critDamage;
      if (milestone.allStats) allStats += milestone.allStats;
    }
  }

  return { attack, defense, health, critChance, critDamage, allStats };
}

/**
 * Get the result of a failed enhancement
 * @param {number} currentStars - Current star count
 * @param {boolean} safeguard - Whether safeguard is enabled
 * @returns {number} New star count after failure
 */
export function getFailureResult(currentStars, safeguard = false) {
  // Safeguard prevents downgrade
  if (safeguard) return currentStars;

  // Stars 0-5: Stay at same level
  if (currentStars < STAR_FORCE_CONFIG.downgradeThreshold) {
    return currentStars;
  }

  // Stars 6+: Drop 1 star (minimum 5)
  return Math.max(STAR_FORCE_CONFIG.minimumStarOnDowngrade, currentStars - 1);
}

export default {
  STAR_FORCE_CONFIG,
  STAR_FORCE_BONUSES,
  STAR_FORCE_MILESTONES,
  STAR_ESSENCE_DROPS,
  getStarTier,
  getEnhancementCost,
  getSuccessRate,
  canUseSafeguard,
  calculateStarForceStats,
  getFailureResult,
};
